// TagsModal — manage the company-structure tags (create / rename / delete).
// Edits a local draft of tag objects; nothing persists until "Save tags", when
// the parent diffs the draft against the server list and fires the mutations.
// Cancel / X / Escape discard.

import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Input, Modal } from '@/components/common';
import type { Tag } from '@/api/endpoints/tagsApi';

/** A draft row — no `id` means a not-yet-created tag. */
export type TagDraft = { id?: string; title: string };

interface TagsModalProps {
  open: boolean;
  tags: Tag[];
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: TagDraft[]) => void;
  /** Context labels — default to the company-structure copy. */
  title?: string;
  note?: string;
  emptyText?: string;
  placeholder?: string;
  createLabel?: string;
  saveLabel?: string;
}

const TagsModal = ({
  open,
  tags,
  saving = false,
  onClose,
  onSave,
  title,
  note,
  emptyText,
  placeholder,
  createLabel,
  saveLabel,
}: TagsModalProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<TagDraft[]>([]);
  const [newTag, setNewTag] = useState('');

  // Re-seed the draft each time the modal opens; discard leftovers on close.
  useEffect(() => {
    if (open) {
      setDraft(tags.map((tag) => ({ id: tag.id, title: tag.title })));
      setNewTag('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const createTag = () => {
    const name = newTag.trim();
    if (!name || draft.some((tag) => tag.title.toLowerCase() === name.toLowerCase())) return;
    setDraft([...draft, { title: name }]);
    setNewTag('');
  };

  const onNewTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      createTag();
    }
  };

  const renameTag = (index: number, value: string) =>
    setDraft(draft.map((tag, i) => (i === index ? { ...tag, title: value } : tag)));

  const deleteTag = (index: number) => setDraft(draft.filter((_, i) => i !== index));

  const save = () =>
    onSave(draft.map((tag) => ({ ...tag, title: tag.title.trim() })).filter((tag) => tag.title));

  // The create input doubles as a search — rows narrow as you type; the
  // original index rides along so rename/delete still hit the right row.
  const query = newTag.trim().toLowerCase();
  const shown = draft
    .map((tag, index) => ({ tag, index }))
    .filter(({ tag }) => !query || tag.title.toLowerCase().includes(query));

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon='tag'
      title={title ?? t('structure_action_tags')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='check' onClick={save} loading={saving}>
            {saveLabel ?? t('structure_tags_save')}
          </Button>
        </>
      }
    >
      <div className='tags-modal'>
        <div className='tags-modal__create'>
          <Input
            size='sm'
            placeholder={placeholder ?? t('structure_tags_placeholder')}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={onNewTagKeyDown}
          />
          <Button size='sm' variant='secondary' iconStart='plus' onClick={createTag}>
            {createLabel ?? t('structure_tags_create')}
          </Button>
        </div>

        {shown.length > 0 && (
          <ul className='tags-modal__list'>
            {shown.map(({ tag, index }) => (
              // Index keys are safe here: rows are only appended/removed via the
              // draft handlers and carry no identity beyond their position.
              <li key={index} className='tags-modal__row'>
                <Icon name='tag' size={18} className='tags-modal__row-icon' />
                <Input
                  size='sm'
                  value={tag.title}
                  onChange={(e) => renameTag(index, e.target.value)}
                />
                <IconButton
                  icon='trash-2'
                  className='tags-modal__delete'
                  aria-label={t('structure_tags_delete', { tag: tag.title })}
                  onClick={() => deleteTag(index)}
                />
              </li>
            ))}
          </ul>
        )}

        {draft.length === 0 && (
          <p className='tags-modal__note'>
            <Icon name='tag' size={16} />
            {emptyText ?? t('structure_tags_empty')}
          </p>
        )}

        <p className='tags-modal__note'>
          <Icon name='info' size={16} />
          {note ?? t('structure_tags_note')}
        </p>
      </div>
    </Modal>
  );
};

export default TagsModal;
